import { cx } from "@suse-o11y/class-variance";
import { onIdle } from "@suse-o11y/on-idle";
import type { Record } from "@suse-o11y/record";
import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { Divider } from "~/components/divider";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { Input } from "~/components/input";
import { RadioButtonGroup } from "~/components/radio-button-group";
import { RadioButtonGroupItem } from "~/components/radio-button-group/item";

import type { DashboardVariableKind } from "../../common/dashboard-spec";
import { Widget, WidgetBody, WidgetHeader } from "../_widget";

import { type FormState, VARIABLE_TYPE_OPTIONS } from "./_form-state";
import { ListVariableForm } from "./_list-variable";
import { Preview } from "./_preview";
import { TextVariableForm } from "./_text-variable";

function useFocusInput() {
	const inputRef = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		// the drawer does not allow to autoFocus the input
		// neither .focus() during synchronous effect
		return onIdle(() => {
			inputRef.current?.focus();
		});
	}, []);

	return inputRef;
}

const TYPE_TO_LABEL = {
	TextVariable: "Text variable",
	ListVariable: "List variable",
} satisfies Record<DashboardVariableKind>;

export interface DrawerContentProps {
	className?: string;
	isEditing: boolean;
	state: FormState;
}

export const DrawerContent: React.FC<DrawerContentProps> = ({ className, isEditing, state }) => {
	const nameInputRef = useFocusInput();
	const activeType = useComputed((monitor) => state.fields.type.getActiveBranch(monitor), [state]);

	return (
		<div className={cx("grid grid-cols-2 gap-3", className)}>
			<div className="space-y-4 overflow-y-auto pr-3">
				<FormField state={state.fields.name}>
					<FormLabel asChild>
						<div>
							<FormLabel>Name *</FormLabel>
							<FormFieldReset />
						</div>
					</FormLabel>

					{(monitor, field) => (
						<Input
							name="dashboard-variable-name"
							onChange={(event) => field.setInput(event.target.value)}
							ref={nameInputRef}
							value={field.getInput(monitor)}
						/>
					)}
				</FormField>

				<FormField state={state.fields.label}>
					<FormLabel asChild>
						<div>
							<FormLabel>Display Label</FormLabel>
							<FormFieldReset />
						</div>
					</FormLabel>

					{(monitor, field) => (
						<Input
							name="dashboard-variable-label"
							onChange={(event) => field.setInput(event.target.value)}
							value={field.getInput(monitor)}
						/>
					)}
				</FormField>

				<FormField state={state.fields.description}>
					<FormLabel asChild>
						<div>
							<FormLabel>Description</FormLabel>
							<FormFieldReset />
						</div>
					</FormLabel>

					{(monitor, field) => (
						<Input
							name="dashboard-variable-description"
							onChange={(event) => field.setInput(event.target.value)}
							value={field.getInput(monitor)}
						/>
					)}
				</FormField>

				<FormField state={state.fields.type.active}>
					<FormLabel asChild>
						<div>
							<FormLabel>Type</FormLabel>
							{isEditing && <FormFieldReset />}
						</div>
					</FormLabel>

					{(monitor, field) => (
						<RadioButtonGroup onValueChange={(value) => field.setInput(value)} value={field.getInput(monitor)}>
							{VARIABLE_TYPE_OPTIONS.map((option) => (
								<RadioButtonGroupItem key={option} name="dashboard-variable-type" value={option}>
									{TYPE_TO_LABEL[option]}
								</RadioButtonGroupItem>
							))}
						</RadioButtonGroup>
					)}
				</FormField>

				<Divider className="uppercase tracking-widest">
					{activeType?.kind === "TextVariable" && "Text options"}
					{activeType?.kind === "ListVariable" && "List options"}
				</Divider>

				{activeType?.kind === "TextVariable" && <TextVariableForm state={activeType.value} />}

				{activeType?.kind === "ListVariable" && (
					<ListVariableForm isEditing={isEditing} name={state.fields.name} state={activeType.value} />
				)}
			</div>

			<Widget className="min-h-0">
				<WidgetHeader title="Preview" />

				<WidgetBody card className="overflow-y-auto">
					<Preview name={state.fields.name} state={state} />
				</WidgetBody>
			</Widget>
		</div>
	);
};
